import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit, Self, Renderer2 } from '@angular/core';
import * as ext from '@app/extensions';
import { _tr } from '@app/extensions';
import { DialogRef, ModalComponent, CloseGuard } from 'ngx-modialog-11';
import { BSModalContext } from 'ngx-modialog-11/plugins/bootstrap';
import { AppearanceDataType, AppearanceType } from '../../services/appearance/appearance.enum';
import { APPEARANCE_FONTS, APPEARANCE_SUPPORTED_EXTENSIONS } from '@app/core/dialogs/appearance/constants/appearance-config-data.constant';
import { AppearanceService } from '@app/core/dialogs/appearance/appearance.service';
import { NgOnDestroy } from '@app/shared/services/destroy.service';
import { takeUntil } from 'rxjs/operators';
import { FieldType } from '@app/core/services/package/field/field-type.enum';
import { SignatureInitialService } from '@app/core/services/signature/signature-initial.service';

export class AppearanceContext extends BSModalContext {
  public tabs: { id: AppearanceType; text: string }[];
  public prepareModel: any;

  constructor(
    public phrases: any,
    public title: string,
    public model: any,
    public appearanceType: AppearanceType,
    public callback: Function,
    public fonts: string[],
    public data: any,
    public maxFileSize: number,
    public supportedExtensions: string[],
  ) {
    super();
  }
}

@Component({
  selector: 'app-appearance',
  templateUrl: './appearance.component.html',
  styleUrls: ['./appearance.component.scss'],
  providers: [AppearanceService, NgOnDestroy],
})
export class AppearanceComponent implements OnInit, CloseGuard, OnDestroy, AfterViewInit, ModalComponent<AppearanceContext> {
  public message: string = null;
  public maxFileSize: number = 40 * 1024 * 1024;
  public fonts: string[] = APPEARANCE_FONTS;
  public supportedExtensions: string[] = APPEARANCE_SUPPORTED_EXTENSIONS;
  public dataTypes = AppearanceDataType;
  public context: AppearanceContext;
  public valueTypeMaxLength = 2048;
  public currentDrawColor: string;
  public currentTypeColor: string;
  public isEmptyText: boolean = false;

  private wrongAnswer: boolean;
  private isChanged: boolean = false;
  private isChangedItem: boolean = false;
  private initialMaxLength = 2048; // SPES-114
  private keypUpListener;

  @ViewChild('enter') enterRef;

  constructor(
    @Self() private ngOnDestroy$: NgOnDestroy,
    public dialog: DialogRef<AppearanceContext>,
    public renderer: Renderer2,
    private appearanceService: AppearanceService,
    private signatureInitialService: SignatureInitialService,
  ) {
    this.context = this.dialog.context;
    this.wrongAnswer = true;
    this.dialog.setCloseGuard(this);

    if (this.context && this.context.model && this.context.model.isDefaultValue) {
      this.isChanged = true;
    }
  }

  ngOnInit(): void {
    this.setChangedColor();
    this.initTabs();

    this.getCurrentTypeColor();
    this.getCurrentDrawColor();

    this.initPhrases();

    this.changeTab(this.context.appearanceType);
  }

  ngAfterViewInit() {
    if (this.enterRef) {
      this.keypUpListener = this.renderer.listen('window', 'keyup', this.onKeyUp);
    }
  }

  ngOnDestroy() {
    this.keypUpListener && this.keypUpListener();
  }

  private getCurrentTypeColor(): void {
    this.appearanceService
      .currentTypeColor()
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe((color: string) => {
        this.currentTypeColor = color;

        if (!this.isChangedItem && this.context.model) {
          this.context.model.tabs[AppearanceDataType.Type].styles.color = color;
        }
      });
  }

  private initTabs(): void {
    this.context.tabs = [
      {
        id: AppearanceType.Signature,
        text: 'My Signature',
      },
      {
        id: AppearanceType.Initial,
        text: 'My Initials',
      },
    ];
  }

  private initPhrases(): void {
    this.context.phrases = {
      [FieldType.Enum.Signature]: {
        title: _tr('Signature Appearance'),
        drawHere: _tr('Draw your signature here'),
        legal: _tr('I understand this is a legal representation of my signature.'),
        upload: _tr('Upload an image of your signature'),
      },
      [FieldType.Enum.Initials]: {
        title: _tr('Initials Appearance'),
        drawHere: _tr('Draw your initials here'),
        legal: _tr('I understand this is a legal representation of my initials.'),
        upload: _tr('Upload an image of your initials'),
      },
      limit: _tr('Maximum file size: 30 Mb. Acceptable file formats:  jpg, png, bmp, gif.'),
    };
  }

  private getCurrentDrawColor(): void {
    this.appearanceService
      .currentDrawColor()
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe((color: string) => (this.currentDrawColor = color));
  }

  private setChangedColor(): void {
    if (this.context.data && this.context.data.type !== AppearanceDataType.Image) {
      this.context.data.type === AppearanceDataType.Draw
        ? this.appearanceService.setNewDrawColor(this.context.data.drawData[0][0].color)
        : this.appearanceService.setNewTypeColor(this.context.data.styles.color);
    }
  }

  onApply() {
    this.apply();
  }

  onClose() {
    this.wrongAnswer = false;
    this.dialog.close();
  }

  get canApply() {
    if (this.context.model.currentType === AppearanceDataType.Type) {
      return !ext.onlySpacesOrEmpty(this.context.model.tabs[this.context.model.currentType].data) && this.isChanged;
    }

    return this.context.model.tabs[this.context.model.currentType].data && this.isChanged;
  }

  beforeDismiss(): boolean {
    return true;
  }

  beforeClose(): boolean {
    return this.wrongAnswer;
  }

  onTabSwitch(type) {
    this.context.model.currentType = type;
    this.message = null;

    if (this.context.model.tabs[type].data) {
      this.isChanged = true;
    }
  }

  onSelectedType({ font, tabType, color }) {
    this.context.model.tabs[tabType].styles.font = font;
    this.context.model.tabs[tabType].styles.color = color;
    this.isChanged = true;
  }

  setData(event, tabType) {
    if (!event) {
      return;
    }

    this.message = null;

    this.context.model.tabs[tabType].data = event.data;

    if (event.fileId) {
      this.context.model.tabs[tabType].fileId = event.fileId;
    }

    this.isChanged = event.data ? true : false;

    if (tabType !== this.dataTypes[tabType] && this.enterRef) {
      this.enterRef.nativeElement.focus();
    }
  }

  clear(data, tabType) {
    this.context.model.tabs[tabType].data = null;
    this.isChanged = false;
  }

  changeTab(id) {
    this.context.title = id === AppearanceType.Initial ? FieldType.Enum.Initials : FieldType.Enum.Signature;
    this.context.appearanceType = id;

    this.context.model = this.context.prepareModel(id);

    if (this.context.appearanceType === AppearanceType.Initial) {
      this.valueTypeMaxLength = this.initialMaxLength;
    }
  }

  private onKeyUp = (event) => {
    if (!event) {
      return;
    }
    const keyCode = event.witch || event.keyCode;

    if (keyCode === 13) {
      this.apply();
    }
  };

  private apply() {
    if (!this.validate()) {
      return;
    }

    this.message = null;

    if (this.context.callback) {
      this.context.callback(this.context.model.tabs[this.context.model.currentType], this.context.appearanceType);
      this.signatureInitialService.setChangeAppearance(true);
    }

    this.onClose();
  }

  private validate() {
    if (this.context.model.currentType === AppearanceDataType.Type) {
      if (ext.onlySpacesOrEmpty(this.context.model.tabs[this.context.model.currentType].data)) {
        this.message = _tr('The value cannot be empty');
        return false;
      }
    } else {
      if (!this.context.model.tabs[this.context.model.currentType].data) {
        this.message = _tr('The value cannot be empty');
        return false;
      }
    }

    return true;
  }

  public checkTextState(textState: boolean): void {
    this.isEmptyText = textState;
  }
}
